import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Leaf, Mail, Lock, User, ArrowRight, Stethoscope, CheckCircle, Shield, UserPlus } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const RegisterPage = ({ setUser }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");
  const fromPatient = searchParams.get("from") === "patient";
  const isPatientFlow = refCode || fromPatient;
  const ownerInviteCode = searchParams.get("owner_invite");
  const referralCode = searchParams.get("referral");
  const [loading, setLoading] = useState(false);
  const [therapistName, setTherapistName] = useState("");
  const [healthConsent, setHealthConsent] = useState(false);
  const [charterAccepted, setCharterAccepted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: refCode ? "patient" : ownerInviteCode ? "therapist" : "patient",
    therapistEmail: "",
  });
  const [manualInviteCode, setManualInviteCode] = useState("");
  const [manualTherapistName, setManualTherapistName] = useState("");

  useEffect(() => {
    if (refCode) {
      axios.get(`${API}/auth/verify-invitation/${refCode}`)
        .then(res => setTherapistName(res.data.therapist_name))
        .catch(() => toast.error(t("auth.invalidInvitationCode", "Code d'invitation invalide")));
    }
  }, [refCode]);

  const verifyManualCode = async (code) => {
    if (!code || code.length < 4) { setManualTherapistName(""); return; }
    try {
      const res = await axios.get(`${API}/auth/verify-invitation/${code}`);
      setManualTherapistName(res.data.therapist_name);
    } catch { setManualTherapistName(""); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t("auth.passwordMinLength"));
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: refCode ? "patient" : formData.role,
        health_data_consent: (refCode || formData.role === "patient") ? healthConsent : false,
        charter_accepted: formData.role === "therapist" ? charterAccepted : false,
      };
      
      if (refCode) {
        payload.invitation_code = refCode;
      } else if (isPatientFlow && manualInviteCode) {
        payload.invitation_code = manualInviteCode;
      } else if (formData.role === "patient" && formData.therapistEmail) {
        payload.therapist_email = formData.therapistEmail;
      }

      if (ownerInviteCode && formData.role === "therapist") {
        payload.owner_invite_code = ownerInviteCode;
      }
      if (referralCode && formData.role === "therapist") {
        payload.referral_code = referralCode;
      }
      
      const response = await axios.post(`${API}/auth/register`, payload, {
        withCredentials: true,
      });
      
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      setUser(response.data);
      toast.success(t("auth.accountCreated"));
      
      // Navigate based on role
      if (response.data.role === "therapist") {
        navigate("/therapist");
      } else {
        if (!response.data.is_active) {
          toast.info(t("auth.accountPending"));
        }
        navigate("/patient");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const params = new URLSearchParams();
    params.set("redirect", "/patient");
    if (refCode) params.set("invitation_code", refCode);
    else if (isPatientFlow && manualInviteCode) params.set("invitation_code", manualInviteCode);
    window.location.href = `${API}/auth/google?${params.toString()}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to={isPatientFlow ? (refCode ? `/bienvenue?ref=${refCode}` : "/bienvenue") : "/"} className="flex items-center gap-2 mb-12">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-12 w-auto" />
            <span className="font-heading text-xl font-semibold">{t("app.name")}</span>
          </Link>

          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2">{t("auth.register")}</h1>
          <p className="text-muted-foreground mb-8">
            {t("auth.startJourney")}
          </p>

          {/* Invitation banner */}
          {refCode && therapistName && (
            <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-3" data-testid="invitation-banner">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                  {t("auth.invitedBy", "Invitation de")} <strong>{therapistName}</strong>
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">{t("auth.invitationNote", "Votre compte sera activé après validation par votre thérapeute.")}</p>
              </div>
            </div>
          )}

          {/* Manual invitation code for patients from /bienvenue without ref */}
          {!refCode && isPatientFlow && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Code d'invitation de votre therapeute</label>
              <input
                type="text"
                value={manualInviteCode}
                onChange={e => { setManualInviteCode(e.target.value); verifyManualCode(e.target.value); }}
                placeholder="TCC-XXXX-XXXX-XXXX"
                className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm"
                data-testid="manual-invite-code"
              />
              {manualTherapistName && (
                <div className="mt-2 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm text-emerald-800 dark:text-emerald-300">{t("auth.invitedBy", "Invitation de")} <strong>{manualTherapistName}</strong></span>
                </div>
              )}
              {manualInviteCode.length >= 4 && !manualTherapistName && (
                <p className="mt-1 text-xs text-destructive">Code d'invitation invalide</p>
              )}
            </div>
          )}

          {/* Owner invitation banner */}
          {ownerInviteCode && (
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3" data-testid="owner-invite-banner">
              <UserPlus className="w-5 h-5 text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">{t("auth.ownerInvite", "Invitation confrère/consoeur")}</p>
                <p className="text-xs text-muted-foreground">{t("auth.ownerInviteNote", "Vous êtes invité(e) par le propriétaire de la plateforme. Votre accès sera gratuit.")}</p>
              </div>
            </div>
          )}

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full mb-6 gap-2"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t("auth.registerWith")}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-background text-muted-foreground">{t("common.or")}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!refCode && !isPatientFlow && (
              <div className="space-y-2">
                <Label>{t("auth.iAm")}</Label>
                <RadioGroup
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="therapist" id="therapist" data-testid="role-therapist" />
                    <Label htmlFor="therapist" className="cursor-pointer">{t("auth.therapist")}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="patient" id="patient" data-testid="role-patient" />
                    <Label htmlFor="patient" className="cursor-pointer">{t("auth.patient")}</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">{t("auth.fullName")}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Jean Dupont"
                  className="pl-10"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {t("auth.passwordPolicy", "Min. 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre")}
            </p>

            {/* Charter acceptance for therapists */}
            {!refCode && formData.role === "therapist" && (
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800" data-testid="charter-consent-section">
                <input
                  type="checkbox"
                  id="charterConsent"
                  checked={charterAccepted}
                  onChange={(e) => setCharterAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  required
                  data-testid="charter-consent-checkbox"
                />
                <label htmlFor="charterConsent" className="text-sm text-muted-foreground cursor-pointer">
                  <Shield className="w-4 h-4 text-emerald-600 inline mr-1 -mt-0.5" />
                  {t("charter.acceptLabel", "J'ai lu et j'accepte la")}{" "}
                  <Link to="/charter" className="text-primary underline" target="_blank">{t("charter.linkLabel", "Charte du Secret Professionnel")}</Link>
                  {t("charter.acceptSuffix", " et m'engage à respecter la confidentialité des données de mes patients.")}
                </label>
              </div>
            )}

            {(refCode || formData.role === "patient") && (
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30" data-testid="health-consent-section">
                <input
                  type="checkbox"
                  id="healthConsent"
                  checked={healthConsent}
                  onChange={(e) => setHealthConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  data-testid="health-consent-checkbox"
                />
                <label htmlFor="healthConsent" className="text-sm text-muted-foreground cursor-pointer">
                  {t("auth.healthDataConsent", "J'accepte le traitement de mes données de santé dans le cadre de mon suivi thérapeutique, conformément au RGPD.")}{" "}
                  <Link to="/privacy" className="text-primary underline" target="_blank">{t("auth.privacyLink", "Politique de confidentialité")}</Link>
                </label>
              </div>
            )}

            {!refCode && formData.role === "patient" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="therapistEmail">{t("auth.therapistEmail")}</Label>
                  <div className="relative">
                    <Stethoscope className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="therapistEmail"
                      type="email"
                      placeholder="therapeute@email.com"
                      className="pl-10"
                      value={formData.therapistEmail}
                      onChange={(e) => setFormData({ ...formData, therapistEmail: e.target.value })}
                      data-testid="therapist-email-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("auth.therapistEmailHint")}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground bg-secondary/50 p-3 rounded-lg">
                  {t("auth.patientNote")}
                </p>
              </>
            )}

            <Button
              type="submit"
              className="w-full gap-2 bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t("auth.creating") : t("auth.createAccount")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t("auth.hasAccount")}{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              {t("auth.login")}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1758274539654-23fa349cc090"
          alt="Person meditating outdoors"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/20" />
      </div>
    </div>
  );
};

export default RegisterPage;
